/**
 * Order (sotuv) Zod schemalar
 *
 * Kanonik qarorlar:
 *   - PaymentType: CASH, CARD, TRANSFER, MIXED (DEBT olib tashlandi)
 *   - DebtStatus: KUTILMOQDA, TOLANGAN, MUDDATI_OTGAN, QORA_ROYHAT
 *   - ReturnCondition: YAROQLI, YAROQSIZ
 *
 * Qarz endi alohida `Debt` jadvali — Order'da `paidAmount < totalAmount`
 * bo'lsa, qisman to'lov hisoblanadi va Debt yaratiladi.
 */
import { z } from 'zod';
import { uuidSchema } from './common.schema.js';
// ─── Enums ────────────────────────────────────────────────────────
export const orderStatusEnum = z.enum(['PENDING', 'COMPLETED', 'RETURNED', 'CANCELLED']);
export const paymentTypeEnum = z.enum(['CASH', 'CARD', 'TRANSFER', 'MIXED']);
export const debtStatusEnum = z.enum(['KUTILMOQDA', 'TOLANGAN', 'MUDDATI_OTGAN', 'QORA_ROYHAT']);
export const returnConditionEnum = z.enum(['YAROQLI', 'YAROQSIZ']);
// ─── Sale item ────────────────────────────────────────────────────
export const saleItemSchema = z.object({
    productId: uuidSchema,
    /** Variant indeksi (Product.variants[] pozitsiya). null/undefined = parent sotuvi. */
    variantIndex: z.number().int().min(0).max(49).nullable().optional(),
    quantity: z.number().int().min(1, "Miqdor 1 dan kam bo'lmasligi kerak").max(100_000),
    unitPrice: z.number().min(0).max(1_000_000_000),
    discount: z.number().min(0).max(1_000_000_000).default(0),
});
// ─── Create sale ──────────────────────────────────────────────────
export const createSaleSchema = z
    .object({
    items: z.array(saleItemSchema).min(1, 'Kamida 1 ta mahsulot kerak').max(500),
    customerId: uuidSchema.optional(),
    paymentType: paymentTypeEnum,
    cashAmount: z.number().min(0).default(0),
    cardAmount: z.number().min(0).default(0),
    transferAmount: z.number().min(0).default(0),
    discount: z.number().min(0).default(0),
    note: z.string().max(500).optional(),
    // Offline-first deduplication
    offlineId: z.string().max(100).optional(),
})
    .refine((data) => {
    // Total to'lov >= 0 bo'lishi kerak
    const totalPaid = data.cashAmount + data.cardAmount + data.transferAmount;
    return totalPaid >= 0;
}, { message: "To'lov manfiy bo'lmasligi kerak" })
    .refine((data) => {
    // Hisoblangan jami summa
    const itemsTotal = data.items.reduce((sum, item) => sum + (item.unitPrice - item.discount) * item.quantity, 0);
    const finalTotal = itemsTotal - data.discount;
    const totalPaid = data.cashAmount + data.cardAmount + data.transferAmount;
    // Agar qisman to'lov bo'lsa (qarz), customerId majburiy
    if (totalPaid < finalTotal && !data.customerId) {
        return false;
    }
    return true;
}, {
    message: "Qarz uchun mijoz kerak (qisman to'lov bo'lsa customerId majburiy)",
    path: ['customerId'],
});
// ─── Return sale ──────────────────────────────────────────────────
export const returnSaleSchema = z.object({
    items: z
        .array(z.object({
        productId: uuidSchema,
        /** Variant indeksi (Product.variants[] pozitsiya). null/undefined = parent qaytarish. */
        variantIndex: z.number().int().min(0).max(49).nullable().optional(),
        quantity: z.number().int().min(1).max(100_000),
    }))
        .min(1)
        .max(500),
    reason: z.string().min(1, 'Qaytarish sababi kerak').max(1000),
    condition: returnConditionEnum,
});
// ─── Order query ──────────────────────────────────────────────────
export const orderQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    date: z.string().optional(), // ISO date (YYYY-MM-DD)
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    status: orderStatusEnum.optional(),
    paymentType: paymentTypeEnum.optional(),
    customerId: uuidSchema.optional(),
    storeId: uuidSchema.optional(),
    userId: uuidSchema.optional(),
    search: z.string().max(100).optional(),
    // Keyset pagination — oxirgi element kursori (createdAt|id ICHKI base64).
    // Berilganda OFFSET emas, keyset ishlatiladi (10k+ da O(1)).
    cursor: z.string().max(200).optional(),
});
// ─── Pay debt ─────────────────────────────────────────────────────
export const payDebtSchema = z.object({
    debtId: uuidSchema,
    amount: z.number().min(0.01, "To'lov 0 dan katta bo'lishi kerak").max(1_000_000_000),
    paymentType: z.enum(['CASH', 'CARD', 'TRANSFER']), // MIXED yo'q
});
// ─── Debt category (qaerdan kelgan qarz) ─────────────────────────
export const debtCategoryEnum = z.enum([
    'MAHSULOT', // Mahsulot olishi
    'XIZMAT', // Xizmat (yog' almashtirish, ta'mir)
    'ESKI_DAFTAR', // Eski daftardan import
    'AVANS', // Mijoz avansga oldi
    'BOSHQA', // Boshqa sabab
]);
// ─── Create debt manually (sotuvsiz, mustaqil qarz) ──────────────
export const createDebtSchema = z
    .object({
    customerId: uuidSchema,
    amount: z.number().min(0.01).max(1_000_000_000),
    paidAmount: z.number().min(0).max(1_000_000_000).optional(), // dastlabki to'langan summa
    currency: z.enum(['UZS', 'USD']).default('UZS'),
    category: debtCategoryEnum.default('BOSHQA'),
    // Muddatli kategoriya (debt_categories) — berilsa dueDate avtomat:
    // dueDate = debtDate + category.termDays
    debtCategoryId: uuidSchema.nullable().optional(),
    debtDate: z.string().datetime().optional(), // o'tmishdagi sana
    dueDate: z.string().datetime().optional(),
    note: z.string().max(500).optional(),
    // ─── Kredit limit / zalog ────────────────────────────────────
    // Limitdan oshganda: zalog kiritiladi (mijozning collateralValue/Note
    // yangilanadi, limitni kengaytiradi) yoki EGASI override qiladi.
    collateralValue: z.number().min(0).max(1_000_000_000).optional(), // yangi zalog qiymati
    collateralNote: z.string().max(500).optional(), // zalog tavsifi
    overrideLimit: z.boolean().optional(), // faqat EGASI — limitni majburan o'tkazish
})
    .refine((data) => (data.paidAmount ?? 0) <= data.amount, {
    message: "To'langan summa qarz summasidan ko'p bo'lmasligi kerak",
    path: ['paidAmount'],
});
// ─── Update debt (qarzni tahrirlash — meta + summa) ──────────────
// To'langan summani BU yerda o'zgartirib bo'lmaydi (alohida payDebt endpoint)
export const updateDebtSchema = z.object({
    amount: z.number().min(0.01).max(1_000_000_000).optional(),
    currency: z.enum(['UZS', 'USD']).optional(),
    category: debtCategoryEnum.optional(),
    // Muddatli kategoriya — berilsa dueDate qayta hisoblanadi (mavjud qarz uchun ham)
    debtCategoryId: uuidSchema.nullable().optional(),
    debtDate: z.string().datetime().optional(),
    dueDate: z.string().datetime().nullable().optional(), // null = muddati o'chirilsin
    note: z.string().max(500).optional(),
});
// ─── Debt query (ro'yxat olish) ──────────────────────────────────
export const debtQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    // limit 1000: creditorName tartiblashda client-side natural sort
    // ishlatiladi (E G 83 → 84 → ... → 104), shu uchun barcha qarz (~422)
    // bir vaqtda yuklanishi kerak. 1000 — bir do'kon uchun ko'p emas.
    limit: z.coerce.number().int().min(1).max(1000).default(50),
    customerId: uuidSchema.optional(),
    status: debtStatusEnum.optional(),
    // Muddatli kategoriya filtri: UUID = shu kategoriya, "none" = kategoriyasiz qarzlar
    debtCategoryId: z.string().max(40).optional(),
    search: z.string().max(100).optional(), // creditorName, creditorPhone bo'yicha
    // Telefon bo'yicha ANIQ qidiruv (alohida input) — faqat raqamlar bilan
    // taqqoslanadi (format/bo'shliq/+998 farqiga chidamli).
    phone: z.string().max(30).optional(),
    overdue: z.coerce.boolean().optional(), // dueDate < now AND status != TOLANGAN
    // Sana oralig'i filtri — qaysi sana ustuni bo'yicha (default debtDate).
    dateField: z.enum(['debtDate', 'dueDate']).optional(),
    dateFrom: z.string().datetime().optional(), // ISO, oraliq boshi (gte)
    dateTo: z.string().datetime().optional(), // ISO, oraliq oxiri (lte)
    sortBy: z
        .enum(['debtDate', 'dueDate', 'amount', 'createdAt', 'creditorName'])
        .default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    // Keyset pagination — oxirgi element kursori (createdAt|id ICHKI base64).
    // Berilganda OFFSET emas, keyset ishlatiladi (10k+ da O(1)).
    cursor: z.string().max(200).optional(),
});
// ─── Bulk: bir nechta qarzga kategoriya biriktirish / chiqarish ──────
// debtCategoryId = uuid → tanlangan qarzlarga shu kategoriya (dueDate
// avtomat qayta hisoblanadi). debtCategoryId = null → kategoriyadan
// chiqarish (dueDate tegilmaydi).
export const bulkAssignDebtCategorySchema = z.object({
    debtIds: z.array(uuidSchema).min(1, 'Kamida 1 ta qarz tanlang').max(1000),
    debtCategoryId: uuidSchema.nullable(),
});
//# sourceMappingURL=order.schema.js.map